/**
 * Settings → Account: a warning while someone is trying to recover (i.e. remove) this account's two-factor
 * authentication without the authenticator. Any signed-in device can cancel the request; Identity then
 * e-mails the owner a confirmation. Shows nothing when no request is waiting (or Identity can't say).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Alert, Button, buttonClass, ErrorAlert } from "@/components/ui";
import { identity } from "@/lib/api";
import { formatDateTime } from "@/lib/format";

export function MfaRecoveryNotice() {
  const queryClient = useQueryClient();
  const status = useQuery({
    queryKey: ["mfa-recovery"],
    queryFn: identity.mfaRecoveryStatus,
    retry: false, // an Identity without recovery answers 404: simply show nothing
  });
  const cancel = useMutation({
    mutationFn: identity.cancelMfaRecovery,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["mfa-recovery"] }),
  });
  const request = status.data;

  if (!request?.pending) {
    return cancel.isSuccess ? (
      <Alert tone="success">
        The recovery request was canceled. Your two-factor authentication is unchanged, and we&apos;ve
        e-mailed you to confirm.
      </Alert>
    ) : null;
  }

  const unfamiliar = request.risk_signals.length > 0;
  return (
    <Alert tone="warning">
      <p className="font-medium">Someone asked to remove two-factor authentication from your account.</p>
      <p className="mt-1">
        Requested {formatDateTime(request.requested_at)}.{" "}
        {request.email_confirmed
          ? `It can be completed from ${formatDateTime(request.available_at)}.`
          : "It hasn't been confirmed from your mailbox yet."}
        {unfamiliar && " It came from a device or network that hasn't signed in to your account before."}
      </p>
      <p className="mt-1">
        If that was you, you don&apos;t need to do anything. If it wasn&apos;t, cancel it now and change your
        password.
      </p>
      <div className="mt-2">
        <ErrorAlert error={cancel.error} />
        <Button
          unstyled
          type="button"
          className={buttonClass("dangerOutline", "lg")}
          disabled={cancel.isPending}
          aria-busy={cancel.isPending}
          onClick={() => cancel.mutate()}
        >
          {cancel.isPending ? "Canceling…" : "Cancel recovery request"}
        </Button>
      </div>
    </Alert>
  );
}
